//@ts-nocheck
import { generateClient } from "aws-amplify/api";
import { Permission, Role } from "db/definitions";
import { createRole as createLocalRole, createRoleForResolver, deleteRole as deleteLocalRole, fetchRolesAndPerms, putRole } from "db/queries/roleQueries";
import { createRole as createRoleQuery, deleteRole as deleteRoleQuery, updateRole as updateRoleQuery } from "graphql/mutations";
import { useEffect, useState } from "react";
import { getRoles } from "utils/getRoles";
import { recordSentry } from "utils/recordSentry";

interface Arguments {
	/**
	 * Whether or not the user is online
	 */
	online: boolean;
}

function useRole(args: Arguments) {
	const { online } = args;
	const [roles, setRoles] = useState<Role[]>([]);

	useEffect(() => {
		async function fetchRoles() {
			if (online) {
				const response = await getRoles();
				setRoles(response);
			} else {
				const response = await fetchRolesAndPerms();
				setRoles(response);
			}
		}
		fetchRoles();
	}, [online]);

	/**
	 * Creates a new role, updating its timestamps and syncing it with the server or local storage as needed.
	 * Updates the local roles state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param role - The role object to be created.
	 * @returns A promise that resolves when the role is created.
	 */
	async function createRole(role: Role): Promise<void> {
		const timestamp = new Date().toISOString();
		role.createdAt = timestamp;
		role.updatedAt = timestamp;
		try {
			const roleForResolver = createRoleForResolver(role);
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				try {
					await client.graphql({
						query: createRoleQuery,
						authMode: "userPool",
						variables: {
							input: roleForResolver,
						},
					});
				} catch (error) {
					console.error(error);
				}
			} else {
				await createLocalRole(roleForResolver);
			}
			const newRoles = [...roles, role];
			setRoles(newRoles);
		} catch (err) {
			recordSentry({ error: err, msg: "Failed to create role " + JSON.stringify(role) });
		}
	}

	/**
	 * Updates an existing role, syncing changes with the server or local storage as needed.
	 * Updates the local roles state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param role - The role object to be updated.
	 * @returns A promise that resolves when the role is updated.
	 */
	async function updateRole(role: Role): Promise<void> {
		try {
			role.updatedAt = new Date().toISOString();
			const roleForResolver = createRoleForResolver(role);
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				await client.graphql({
					query: updateRoleQuery,
					authMode: "userPool",
					variables: {
						roleForResolver,
					},
				});
			} else {
				await putRole(roleForResolver);
			}
			const newRoles = roles.map((roleItem) => (roleItem.id === role.id ? role : roleItem));
			setRoles(newRoles);
		} catch (err) {
			recordSentry({ error: err, msg: "Error updating role " + JSON.stringify(role) });
		}
	}

	/**
	 * Deletes a role, syncing the removal with the server or local storage as needed.
	 * Updates the local roles state upon success.
	 * @author Eric Webb <ewebb@factorearth.com>
	 * @param role - The role object to be deleted.
	 * @returns A promise that resolves when the role is deleted.
	 */
	async function deleteRole(role: Role): Promise<void> {
		try {
			if (online) {
				const client = generateClient({ authMode: "userPool" });
				await client.graphql({
					query: deleteRoleQuery,
					authMode: "userPool",
					variables: {
						id: role.id,
					},
				});
			} else {
				await deleteLocalRole(role.id);
			}
			const newRoles = roles.filter((roleItem) => roleItem.id !== role.id);
			setRoles(newRoles);
		} catch (err) {
			recordSentry({ error: err, msg: "Error deleting role " + JSON.stringify(role) });
		}
	}

	return {
		createRole,
		deleteRole,
		updateRole,
		roles,
	};
}

export default useRole;
